import { inject, Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

import { PRODUCTS_UID } from '../../enums/products.enum';
import { LogService } from '../log/log.service';

import { PluginListenerHandle } from '@capacitor/core';
import { ConnectionStatus } from '@capacitor/network';
import { mqtt } from '@ubie/mqttcommunication';
import { Globals } from '../../globals';
import { CommunicationCoreService } from '../communication/communication-core/communication-core.service';
import { DevicesService } from '../devices/devices.service';

@Injectable({
  providedIn: 'root',
})
export class OfflineCommunicationService implements OnDestroy {
  isStarted = false;
  isWifi = false;
  currentStatus?: ConnectionStatus;

  destroy$: Subject<boolean> = new Subject<boolean>();

  private readonly logService: LogService = inject(LogService);
  private readonly devicesService: DevicesService = inject(DevicesService);
  private readonly communicationCoreService: CommunicationCoreService = inject(
    CommunicationCoreService,
  );
  private readonly globals: Globals = inject(Globals);

  isOfflineMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );
  isOfflineMode = false;

  ubies: Set<string> = new Set<string>();

  isUbieTouch = this.globals.isUbieTouch;

  eventListenerGetUser?: PluginListenerHandle = undefined;
  eventListenerOfflineChanged?: PluginListenerHandle = undefined;

  ngOnDestroy(): void {
    this.destroy$.next(false);
    this.destroy$.complete();
  }

  setOfflineMode(isOfflineMode: boolean): void {
    if (isOfflineMode !== this.isOfflineMode && this.isUbieTouch) {
      this.isOfflineMode = isOfflineMode;
      this.isOfflineMode$.next(isOfflineMode);
    }
  }

  /***
   * get all ubies that have the mqtt bridge product for this user and add it to the offline manager
   */
  private async initV2(): Promise<void> {
    if (!this.isUbieTouch) {
      this.logService.info('offline comm - not ubie touch');
      return;
    }

    this.devicesService
      .getDevicesByProductUid(PRODUCTS_UID.MqttBridge)
      .subscribe({
        next: (ubies) => {
          this.logService.debug('offline comm - got devices', ubies);
          mqtt
            .addOfflineUbies({ ubies: ubies })
            .then(() => {
              this.logService.info('offline comm - init done');
            })
            .catch((ex) =>
              this.logService.error('offline comm - init error', ex.message),
            );
        },
        error: (ex) =>
          this.logService.error('OFFLINE COMM - error getting devices', ex),
      });
  }

  async start(): Promise<void> {
    if (!this.isUbieTouch) {
      return;
    }

    this.listenToMqttState();
    this.initV2();
  }

  listenToMqttState(): void {
    if (!this.isUbieTouch) {
      return;
    }

    this.communicationCoreService.mqttState
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOnline: boolean | null) => {
        if (isOnline === false && this.isOfflineMode === false) {
          this.setOfflineMode(true);
        } else if (isOnline === true && this.isOfflineMode === true) {
          this.setOfflineMode(false);
        }
      });
  }
}
